import clsx from "clsx";
import { useEffect, useState } from "react";

import { useTunnels } from "../../context/tunnels";
import { t, useI18n } from "../../i18n";
import { ExitZenModeButton, UndoRedoActions, ZoomActions } from "../Actions";
import { Section } from "../Section";
import Stack from "../Stack";

import type { ActionManager } from "../../actions/manager";
import type { UIAppState } from "../../types";

const Footer = ({
  appState,
  actionManager,
  showExitZenModeBtn,
  renderWelcomeScreen,
}: {
  appState: UIAppState;
  actionManager: ActionManager;
  showExitZenModeBtn: boolean;
  renderWelcomeScreen: boolean;
}) => {
  const { FooterCenterTunnel, WelcomeScreenHelpHintTunnel } = useTunnels();
  const { langCode } = useI18n();
  const isZh = langCode.toLowerCase().startsWith("zh");
  const hints = isZh
    ? [
        "滚轮缩放画布；按住空格键拖动可平移画布。",
        "先在左侧选工具，再在画布上拖拽绘制（矩形/箭头/文字等）。",
        "按住 Shift 绘制可保持水平/垂直/45°，也能画出正圆/正方形。",
        "双击形状或文字可快速编辑；按 Esc 退出编辑。",
        "选中元素后，用右侧面板调整颜色、粗细、透明度和图层。",
        "误操作了就点底部的撤销/重做，随时可以回退。",
        "完成后建议经常保存到文件，避免浏览器缓存被清理导致丢失。",
        "需要快捷键和帮助，点右下角“帮助”。",
      ]
    : [
        "Use mouse wheel to zoom; hold Space and drag to pan the canvas.",
        "Pick a tool from the left toolbar, then drag on the canvas to draw.",
        "Hold Shift to constrain angles or create perfect squares/circles.",
        "Double-click to edit; press Esc to exit editing.",
        "After selecting an element, use the right panel to tweak style and order.",
        "Use Undo/Redo in the bottom bar to revert mistakes.",
        "Save often to avoid losing work if browser storage gets cleared.",
        "Need shortcuts and help? Use the Help button at the bottom right.",
      ];
  const [hintIndex, setHintIndex] = useState(0);

  useEffect(() => {
    if (hints.length <= 1) {
      return;
    }

    const interval = window.setInterval(() => {
      setHintIndex((prev) => {
        return (prev + 1) % hints.length;
      });
    }, 5000);

    return () => window.clearInterval(interval);
  }, [hints.length]);

  return (
    <footer
      role="contentinfo"
      className="layer-ui__wrapper__footer App-menu App-menu_bottom"
    >
      <div
        className={clsx("layer-ui__wrapper__footer-left zen-mode-transition", {
          "layer-ui__wrapper__footer-left--transition-left":
            appState.zenModeEnabled,
        })}
      >
        <Stack.Col gap={2}>
          <Section heading="canvasActions">
            <ZoomActions
              renderAction={actionManager.renderAction}
              zoom={appState.zoom}
            />

            {!appState.viewModeEnabled && (
              <UndoRedoActions
                renderAction={actionManager.renderAction}
                className={clsx("zen-mode-transition", {
                  "layer-ui__wrapper__footer-left--transition-bottom":
                    appState.zenModeEnabled,
                })}
              />
            )}
          </Section>
        </Stack.Col>
      </div>
      <div
        className={clsx("zhirai-footer-center zen-mode-transition", {
          "layer-ui__wrapper__footer-left--transition-bottom":
            appState.zenModeEnabled,
        })}
      >
        {hints.length > 0 && (
          <div className="zhirai-footer-hint-inline" aria-live="polite">
            <span key={hintIndex} className="zhirai-footer-hint-inline__text">
              {hints[hintIndex % hints.length]}
            </span>
          </div>
        )}
        <FooterCenterTunnel.Out />
      </div>
      <ExitZenModeButton
        actionManager={actionManager}
        showExitZenModeBtn={showExitZenModeBtn}
      />
    </footer>
  );
};

export default Footer;
Footer.displayName = "Footer";
